// LivestreamOverlayFooter.jsx
import React, {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Loading from '../component/Loading.jsx';
import PrivateShowStatusTitle from '../container/PrivateShowStatusTitle.js';

import { privateShowGiftType } from '../resource/liveStreamConstants.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import numberWithCommas from '../resource/numberWithCommas.js';
import { getCanUnlockByTicket } from '../resource/livestreamUtils.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { payment as paymentDebug } from '../resource/debug.js';

import { color, textColor } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ButtonShimmerStyle from '../style/ButtonShimmerStyle.js';
import DiamondIconSrc from '../../img/point_2.svg';

import { Entrypoint } from '../resource/shopConstants.js';
import getQuickpayCtaTranslationKey from '../resource/getQuickpayCtaTranslationKey.js';

const log = paymentDebug.extend('log:LivestreamOverlayFooter');
const errorLog = paymentDebug.extend('error:LivestreamOverlayFooter');
const LIVESTREAM_NS = { ns: TranslationNamespace.LIVESTREAM };

export const LivestreamOverlayFooter = ({
  balance = 0,
  fundingId = '',
  giftSendingStatus = null,
  giftId = null,
  giftPrice = null,
  hasBalance = false,
  isQuickpay = false,
  purchaseCount = 0,
  hasStreamToken = false,
  isAuthed = false,
  isBalanceFetching = false,
  isGiftFetched = false,
  isPrivateLiveStreamStarted = false,
  isPrivateShow = true,
  isPrivateStreamUnlocked = false,
  isSendGiftFetching = false,
  isUnlockModal = false,
  mixpanelModalId = null,
  performDuration = 0,
  performingId = '',
  playerStatus = null,
  price = null,
  priceDurationMinutes = 1,
  privateLiveStreamStartTimestamp = 0,
  privateShowStatus = null,
  productId = null,
  sendGiftFetchError = null,
  shouldShowCountdownWhenPerforming = false,
  shouldShowLivestreamCta = false,
  shouldShowLoadingOnUnlockButton = false,
  streamId = '',
  streamStatus = null,
  streamTokenDecodedData = null,
  addvalue = NULL_FUNCTION,
  fetchGift = NULL_FUNCTION,
  pushToastr = NULL_FUNCTION,
  sendLiveStreamGift = NULL_FUNCTION,
  unlockPrivateStream = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const [isSendGiftSuccess, setIsSendGiftSuccess] = useState(false);
  const previousGiftSendingStatus = useRef(giftSendingStatus);

  const performDurationInMinutes = Math.floor(performDuration / 60);
  const showPrice = giftPrice ?? price;
  const needToAddvalue = isAuthed && showPrice != null && balance < showPrice;
  const needToAddvalueForPrivateStream =
    hasBalance && price != null ? balance < price : false;
  const availableMinsForPrivateStream =
    hasBalance && price != null
      ? Math.floor((balance / price) * priceDurationMinutes) || 0
      : 0;
  const canUnlockByTicket = getCanUnlockByTicket({
    productId: giftId,
    productPrice: giftPrice,
  });
  const shouldShowShimmer =
    !isAuthed ||
    (isQuickpay && (needToAddvalue || needToAddvalueForPrivateStream));

  useEffect(() => {
    if (isPrivateShow && giftId && !isGiftFetched) {
      fetchGift({ giftId });
    }
  }, [fetchGift, giftId, isGiftFetched, isPrivateShow]);

  useEffect(() => {
    setIsSendGiftSuccess(false);
    previousGiftSendingStatus.current = null;
  }, [fundingId, giftId, performingId, streamId]);

  useEffect(() => {
    if (
      previousGiftSendingStatus.current === 'sending' &&
      giftSendingStatus === 'success' &&
      !sendGiftFetchError
    ) {
      setIsSendGiftSuccess(true);
    }

    previousGiftSendingStatus.current = giftSendingStatus;
  }, [giftSendingStatus, sendGiftFetchError]);

  const handleUnlockPrivateShow = useCallback(() => {
    const privateShowData = {
      modalId: mixpanelModalId,
      type: privateShowGiftType.SINGLE,
      privateShowStatus,
      playerStatus,
      streamStatus,
      tokenNbf: streamTokenDecodedData?.nbf,
      tokenExp: streamTokenDecodedData?.exp,
      fundingId,
    };

    log('handleUnlockPrivateShow', {
      streamId,
      giftId,
      isUnlockModal,
      ...privateShowData,
    });
    return sendLiveStreamGift({
      streamId,
      giftId,
      modalId: mixpanelModalId,
      privateShowData,
    });
  }, [
    fundingId,
    giftId,
    isUnlockModal,
    playerStatus,
    privateShowStatus,
    mixpanelModalId,
    sendLiveStreamGift,
    streamId,
    streamStatus,
    streamTokenDecodedData,
  ]);

  const handleUnlockPrivateStream = useCallback(() => {
    if (price == null) {
      return addvalue({ entrypoint: Entrypoint.PRIVATE, via: 'insufficient' });
    }
    if (needToAddvalueForPrivateStream) {
      errorLog('insufficient funds, redirect to addvalue', { streamId });
      pushToastr({
        textKey: 'alert_insufficient_funds',
        color: 'error',
      });
      return addvalue({ entrypoint: Entrypoint.PRIVATE, via: 'insufficient' });
    }
    log('handleUnlockPrivateStream', { streamId });
    return unlockPrivateStream({ streamId });
  }, [
    addvalue,
    needToAddvalueForPrivateStream,
    price,
    pushToastr,
    streamId,
    unlockPrivateStream,
  ]);

  const trackingButtonId = useMemo(() => {
    if (!isAuthed) return ButtonId.Detail.ButtonDetailSignup;
    if (isPrivateShow) {
      return needToAddvalue
        ? ButtonId.LiveStream.CountDownToDiamondShop
        : ButtonId.LiveStream.ButtonShowPurchaseModal;
    }
    return needToAddvalueForPrivateStream
      ? ButtonId.LiveStream.CountDownToDiamondShop
      : ButtonId.LiveStream.ButtonTurnToPrivate;
  }, [isAuthed, isPrivateShow, needToAddvalue, needToAddvalueForPrivateStream]);

  const getPrivateStreamActionButtonTextKey = useCallback(() => {
    if (productId) return 'campaign_buy_livestream_package';
    if (needToAddvalueForPrivateStream) {
      return 'livestream_detail_insufficient_balance_go_to_topup';
    }
    return isPrivateLiveStreamStarted
      ? 'livestream_detail_pay_to_join'
      : 'button_pay_for_stream';
  }, [isPrivateLiveStreamStarted, needToAddvalueForPrivateStream, productId]);

  const getPrivateShowActionButtonTextKey = useCallback(() => {
    if (isSendGiftSuccess) {
      return 'ticket_purchase_successful';
    }
    if (needToAddvalue) {
      return 'livestream_detail_insufficient_balance_go_to_topup';
    }
    return 'livestream_detail_ticket_required';
  }, [isSendGiftSuccess, needToAddvalue]);

  const getActionButtonContent = () => {
    if (
      !isAuthed ||
      (isQuickpay && (needToAddvalue || needToAddvalueForPrivateStream))
    ) {
      return t(
        getQuickpayCtaTranslationKey({
          base: isPrivateShow
            ? 'join_this_show'
            : 'join_private_livestream_detail',
          purchaseCount,
        }),
        LIVESTREAM_NS
      );
    }
    if (isSendGiftFetching || isBalanceFetching || !showPrice) {
      return <Loading />;
    }
    return t(
      isPrivateShow
        ? getPrivateShowActionButtonTextKey()
        : getPrivateStreamActionButtonTextKey(),
      LIVESTREAM_NS
    );
  };

  const isActionButtonDisabled = useMemo(
    () =>
      showPrice == null ||
      isBalanceFetching ||
      (isPrivateShow && (isSendGiftFetching || isSendGiftSuccess)),
    [
      isBalanceFetching,
      isPrivateShow,
      isSendGiftFetching,
      isSendGiftSuccess,
      showPrice,
    ]
  );

  const privateStreamTrackingPayload = useMemo(() => {
    return {
      modalId: mixpanelModalId,
      'stream.id': streamId,
      'liveStream.price': price,
      'liveStream.priceDurationMinutes': priceDurationMinutes,
      'liveStream.hasBalance': hasBalance,
      'liveStream.hasStreamToken': hasStreamToken,
      'liveStream.isPrivateStreamUnlocked': isPrivateStreamUnlocked,
      'liveStream.isPrivateLiveStreamStarted': isPrivateLiveStreamStarted,
      'liveStream.isScheduledPrivateLiveStream':
        !!privateLiveStreamStartTimestamp,
      'liveStream.privateLiveStreamStartTimestamp':
        privateLiveStreamStartTimestamp,
      'liveStream.shouldShowLoadingUnlockButton':
        shouldShowLoadingOnUnlockButton,
    };
  }, [
    mixpanelModalId,
    streamId,
    price,
    priceDurationMinutes,
    hasBalance,
    hasStreamToken,
    isPrivateStreamUnlocked,
    isPrivateLiveStreamStarted,
    privateLiveStreamStartTimestamp,
    shouldShowLoadingOnUnlockButton,
  ]);

  const privateShowTrackingPayload = useMemo(() => {
    return {
      modalId: mixpanelModalId,
      'gift.id': giftId,
      'gift.price': showPrice,
      'show.ticket.type_selected': privateShowGiftType.SINGLE,
    };
  }, [mixpanelModalId, giftId, showPrice]);

  const handleOnClick = useCallback(
    event => {
      event.preventDefault();
      if (!isAuthed || needToAddvalue || needToAddvalueForPrivateStream) {
        return addvalue({
          entrypoint: isPrivateShow ? Entrypoint.SHOW : Entrypoint.PRIVATE,
          via: 'livestream',
        });
      }
      if (isPrivateShow) {
        return handleUnlockPrivateShow();
      }
      return handleUnlockPrivateStream(event);
    },
    [
      addvalue,
      handleUnlockPrivateShow,
      handleUnlockPrivateStream,
      isAuthed,
      isPrivateShow,
      needToAddvalue,
      needToAddvalueForPrivateStream,
    ]
  );

  const liveStreamInfo = useMemo(
    () => (
      <InfoWrapper>
        {isPrivateShow ? (
          shouldShowCountdownWhenPerforming ? (
            <PrivateShowStatusTitle
              streamId={streamId}
              shouldShowCountdownWhenPerforming={
                shouldShowCountdownWhenPerforming
              }
            />
          ) : (
            `${t('show_duration', LIVESTREAM_NS)} ${t('minute_unit', {
              ...[performDurationInMinutes],
              ...LIVESTREAM_NS,
            })}`
          )
        ) : (
          t('info_remaining_time_private_stream', {
            0: availableMinsForPrivateStream,
            ...LIVESTREAM_NS,
          })
        )}
      </InfoWrapper>
    ),
    [
      availableMinsForPrivateStream,
      isPrivateShow,
      performDurationInMinutes,
      shouldShowCountdownWhenPerforming,
      streamId,
      t,
    ]
  );

  const priceWithIcon = useMemo(
    () => (
      <PriceWithIcon>
        <img src={DiamondIconSrc} alt="Diamond" aria-hidden />
        <PriceValue>
          {numberWithCommas({ number: isPrivateShow ? showPrice : price })}
        </PriceValue>
      </PriceWithIcon>
    ),
    [isPrivateShow, price, showPrice]
  );

  const priceUnit = useMemo(
    () =>
      isPrivateShow
        ? t('livestream_detail_per_ticket', {
            number: 1,
            ...LIVESTREAM_NS,
          })
        : t('private_stream_per_second', {
            second: priceDurationMinutes * 60,
            ...LIVESTREAM_NS,
          }),
    [isPrivateShow, priceDurationMinutes, t]
  );

  return (
    <StyledPrivateOverlayFooter>
      {(isPrivateShow || canUnlockByTicket || shouldShowLivestreamCta) && (
        <ActionButton
          data-element_id={trackingButtonId}
          data-tracking_payload={
            isPrivateShow
              ? privateShowTrackingPayload
              : privateStreamTrackingPayload
          }
          onClick={handleOnClick}
          disabled={isActionButtonDisabled}
          isPurple={!isPrivateShow}
          shouldShowShimmer={shouldShowShimmer}
        >
          {getActionButtonContent()}
        </ActionButton>
      )}
      <InfoRow>
        {liveStreamInfo}
        {priceWithIcon}
        {priceUnit}
      </InfoRow>
    </StyledPrivateOverlayFooter>
  );
};

LivestreamOverlayFooter.propTypes = {
  balance: PropTypes.number,
  fundingId: PropTypes.string,
  giftSendingStatus: PropTypes.string,
  giftId: PropTypes.string,
  giftPrice: PropTypes.number,
  hasBalance: PropTypes.bool,
  isQuickpay: PropTypes.bool,
  purchaseCount: PropTypes.number,
  hasStreamToken: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isBalanceFetching: PropTypes.bool,
  isGiftFetched: PropTypes.bool,
  isPrivateLiveStreamStarted: PropTypes.bool,
  isPrivateShow: PropTypes.bool,
  isPrivateStreamUnlocked: PropTypes.bool,
  isSendGiftFetching: PropTypes.bool,
  isUnlockModal: PropTypes.bool,
  mixpanelModalId: PropTypes.string,
  performDuration: PropTypes.number,
  performingId: PropTypes.string,
  playerStatus: PropTypes.string,
  price: PropTypes.number,
  priceDurationMinutes: PropTypes.number,
  privateLiveStreamStartTimestamp: PropTypes.number,
  privateShowStatus: PropTypes.string,
  productId: PropTypes.string,
  sendGiftFetchError: PropTypes.object,
  shouldShowCountdownWhenPerforming: PropTypes.bool,
  shouldShowLivestreamCta: PropTypes.bool,
  shouldShowLoadingOnUnlockButton: PropTypes.bool,
  streamId: PropTypes.string,
  streamStatus: PropTypes.string,
  streamTokenDecodedData: PropTypes.shape({
    nbf: PropTypes.number,
    exp: PropTypes.number,
  }),
  addvalue: PropTypes.func,
  fetchGift: PropTypes.func,
  pushToastr: PropTypes.func,
  sendLiveStreamGift: PropTypes.func,
  unlockPrivateStream: PropTypes.func,
};

const StyledPrivateOverlayFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const ActionButton = styled(({ isPurple, shouldShowShimmer, ...restProps }) => (
  <button {...restProps} />
))`
  ${ResetButtonStyle};
  ${({ shouldShowShimmer }) => shouldShowShimmer && ButtonShimmerStyle};
  border-radius: 4px;
  width: 100%;
  height: 32px;
  background-image: ${({ isPurple }) =>
    isPurple
      ? `linear-gradient(135deg, ${color.gamePurple} 0%, ${color.gamePurpleDeep} 100%)`
      : `linear-gradient(to right, ${color.mvpPink} 0%, ${color.mvpGold})`};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: ${textColor.white};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: ${color.neutral['100_alpha_80']};
`;

const InfoWrapper = styled.div`
  white-space: nowrap;
`;

const PriceWithIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${color.tealBlue};
  gap: 2px;
  > img {
    width: 14px;
    height: 14px;
    object-fit: contain;
  }
`;

const PriceValue = styled.div`
  font-weight: 600;
`;

export default LivestreamOverlayFooter;
